<!-- MARK: GENERAL SETTINGS -->
<app-section sectionTitle="General Settings">
  <app-subsection>
    <div style="margin-bottom: 10px; display: flex; flex-direction: column">
      <p>Loaded Craft Group: {{ craftCalculatorStateService.craftGroup().name() }}</p>
      <div style="margin-bottom: 10px; display: flex; flex-wrap: wrap; align-items: center">
        <mat-form-field style="width: 180px" subscriptSizing="dynamic">
          <mat-label>Server</mat-label>
          <mat-select [(ngModel)]="craftCalculatorStateService.craftGroup().server">
            @for (server of craftCalculatorStateService.servers; track server.id) {
              <mat-option [value]="server.id">{{ server.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field style="max-width: 120px" subscriptSizing="dynamic">
          <mat-label>Average Days</mat-label>
          <input matInput type="number" step="1" min="3" max="30" [(ngModel)]="craftCalculatorStateService.craftGroup().averageNumberOfDays" />
        </mat-form-field>
        <div style="display: flex; flex-direction: column">
          <mat-checkbox style="margin-right: 10px" [(ngModel)]="craftCalculatorStateService.craftGroup().hasPremium">Has Premium</mat-checkbox>
        </div>
        <button color="accent" mat-fab extended matTooltip="Reset" (click)="resetCraftGroup()" aria-label="Reset Craft Group"><mat-icon>close</mat-icon>Reset</button>
      </div>
    </div>
  </app-subsection>
</app-section>

<app-section sectionTitle="Add Crafts">
  <!-- MARK: Add a craft -->
  <app-subsection subSectionTitle="Choose a Craft Group">
    <div style="margin-bottom: 10px; display: flex; flex-wrap: wrap; align-items: start">
      <mat-form-field style="width: 420px">
        <mat-label>Add a Craft Group</mat-label>
        <mat-select (selectionChange)="craftCalculatorStateService.addCrafts($event.value)">
          <mat-option>
            <ngx-mat-select-search [(ngModel)]="itemsFilterText" placeholderLabel="Search" noEntriesFoundLabel="No matches"></ngx-mat-select-search>
          </mat-option>
          @for (item of filteredCraftableItems(); track $index) {
            <mat-option [value]="item">{{ item.localizedName }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>
  </app-subsection>

  <!-- MARK: Filters -->
  <app-subsection subSectionTitle="Crafts Filter">
    <div style="display: flex; flex-direction: column; gap: 16px">
      <div style="display: flex; flex-wrap: wrap; align-items: start">
        <!-- Tiers -->
        <mat-form-field style="width: 180px">
          <mat-label>Tiers</mat-label>
          <mat-select [(ngModel)]="craftCalculatorStateService.craftsSelectedTiers" multiple>
            @for (tier of craftCalculatorStateService.tiers; track $index) {
              <mat-option [value]="tier">{{ tier }}</mat-option>
            }
          </mat-select>
          @if (craftCalculatorStateService.craftsSelectedTiers().length > 0) {
            <button matSuffix mat-icon-button aria-label="Clear" (click)="craftCalculatorStateService.craftsSelectedTiers.set([])" aria-label="Clear Craft Tiers Filter">
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
        <!-- Enchantments -->
        <mat-form-field style="width: 180px">
          <mat-label>Enchantments</mat-label>
          <mat-select [(ngModel)]="craftCalculatorStateService.craftsSelectedEnchantments" multiple>
            @for (ench of craftCalculatorStateService.enchantments; track $index) {
              <mat-option [value]="ench">{{ ench }}</mat-option>
            }
          </mat-select>
          @if (craftCalculatorStateService.craftsSelectedEnchantments().length > 0) {
            <button matSuffix mat-icon-button aria-label="Clear" (click)="craftCalculatorStateService.craftsSelectedEnchantments.set([])" aria-label="Clear Craft EnchantmentsFilter">
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
        <!-- Checkboxes -->
        <div style="display: flex; flex-direction: column">
          <mat-checkbox style="margin-right: 10px" [(ngModel)]="craftCalculatorStateService.craftGroup().hideMissingPrices">Hide Missing Prices</mat-checkbox>
          <mat-checkbox style="margin-right: 10px" [(ngModel)]="craftCalculatorStateService.craftGroup().onlyMissingPricesCrafts">Only Missing Prices</mat-checkbox>
        </div>
        <div style="display: flex; flex-direction: column">
          <mat-checkbox style="margin-right: 10px" [(ngModel)]="craftCalculatorStateService.craftGroup().hideUnselectedCrafts">Hide Unselected Crafts</mat-checkbox>
          <mat-checkbox style="margin-right: 10px" [(ngModel)]="craftCalculatorStateService.craftGroup().onlyShowMainRecipes">Only Main Recipes</mat-checkbox>
        </div>
        <div style="display: flex; flex-direction: column">
          <mat-checkbox style="margin-right: 10px" [(ngModel)]="craftCalculatorStateService.craftGroup().hideFocusInfo">Hide Focus Info</mat-checkbox>
          <mat-checkbox style="margin-right: 10px" [(ngModel)]="craftCalculatorStateService.craftGroup().hideFameInfo">Hide Fame Info</mat-checkbox>
        </div>
        <div style="display: flex; flex-direction: column">
          <mat-checkbox style="margin-right: 10px" [(ngModel)]="craftCalculatorStateService.craftGroup().hideConsumablesPicker">Hide Consumables Picker</mat-checkbox>
        </div>
      </div>
      <mat-form-field style="width: 98%">
        <mat-label>Text Filter</mat-label>
        <input matInput type="text" [(ngModel)]="searchCraftsText" />
        @if (searchCraftsText()) {
          <button matSuffix mat-icon-button aria-label="Clear" (click)="searchCraftsText.set('')" aria-label="Clear Crafts Search">
            <mat-icon>close</mat-icon>
          </button>
        }
      </mat-form-field>
    </div>
  </app-subsection>

  <!-- MARK: Summary & Delete -->
  <app-subsection subSectionTitle="Summary & Delete">
    <p>
      Crafts:
      {{ craftCalculatorStateService.craftGroup().craftsCount() }}
      ({{ craftsDataSource().data.length }}
      visible,
      {{ craftCalculatorStateService.craftGroup().selectedCraftsCount() }}
      selected,
      {{ craftCalculatorStateService.craftGroup().missingPricesCount() }}
      missing prices)
    </p>
    <div style="display: flex">
      @for (item of craftCalculatorStateService.craftGroup().craftCategoryTopItems(); track item.uniqueName) {
        <div style="display: flex; flex-direction: row; align-items: center; padding-right: 5px; padding-top: 3px">
          <app-item-image [item]="item" [size]="50" />
          <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Delete This Category" (click)="craftCalculatorStateService.craftGroup().deleteCraftCategory(item.groupUniqueName)">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      }
    </div>
    <p>To find out your RRR, check out the <a matTooltip="Click to check the wiki page for RRR" href="https://wiki.albiononline.com/wiki/Resource_Return_Rate" target="_blank">wiki</a>.</p>
  </app-subsection>

  <!-- MARK: Crafts Table -->
  <app-subsection subSectionTitle="Crafts Table">
    @defer {
      <div class="table mat-elevation-z8">
        <table mat-table [dataSource]="craftsDataSource()" #craftsSort="matSort" matSort matSortStart="desc" [trackBy]="trackByCraftId">
          <!-- Product -->
          <ng-container matColumnDef="product">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-160">Product</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; align-items: center">
                <div style="display: flex; flex-direction: column; align-items: center; padding-right: 5px; padding-top: 3px">
                  <app-item-image [item]="craft.product" [size]="50" [hideTooltip]="true" />
                </div>
                <div style="display: flex; flex-direction: column; align-items: start; text-align: start">
                  <div>
                    {{ craft.product.localizedName }}
                  </div>
                  <div>
                    <app-copy-text-button [text]="craft.product.localizedName"></app-copy-text-button>
                    <app-check-price-button [itemUniqueName]="craft.product.uniqueName"></app-check-price-button>
                  </div>
                </div>
                <div>
                  @if (craft.hasMissingPrices()) {
                    <mat-icon style="color: var(--accent-color); margin-left: 10px" [matTooltip]="generateMissingPricesText(craft.missingPricesItemsNames())" matTooltipClass="multiline-tooltip"> warning </mat-icon>
                  }
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Craft Location -->
          <ng-container matColumnDef="craftlocation">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-240">Craft Location</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <mat-form-field style="width: 240px">
                  <mat-label>Craft Location</mat-label>
                  <mat-select [(ngModel)]="craft.craftLocation">
                    @for (location of craftCalculatorStateService.craftLocations; track location.index) {
                      <mat-option [value]="location">{{ location.displayName }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateCraftLocation(craft.craftLocation())">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateCraftLocation(craft.craftLocation(), craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Consumables -->
          <ng-container matColumnDef="consumables">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Consumables</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <div style="display: flex; flex-direction: column; align-items: center; justify-content: center">
                  <mat-form-field style="width: 280px">
                    <mat-label>Add a Consumable</mat-label>
                    <mat-select (selectionChange)="craft.addConsumable($event.value)">
                      <mat-option>
                        <ngx-mat-select-search [(ngModel)]="consumablesFilterText" placeholderLabel="Search" noEntriesFoundLabel="No matches"></ngx-mat-select-search>
                      </mat-option>
                      @for (item of filteredConsumableItems(); track item.uniqueName) {
                        <mat-option [value]="item">{{ item.localizedName }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <div style="display: flex; flex-direction: column; align-items: flex-start; justify-content: center">
                    @for (consumable of craft.consumables(); track consumable.inputItem.item.uniqueName) {
                      <div style="display: flex; flex-direction: row; align-items: center; justify-content: space-between; margin-bottom: 5px; width: 100%">
                        <span style="flex-shrink: 0; min-width: 120px; text-align: left; padding-right: 4px">{{ consumable.inputItem.item.localizedName }}</span>
                        <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                          <mat-form-field style="width: 80px; margin-right: 8px; margin-top: 0px" appearance="outline" subscriptSizing="dynamic">
                            <input matInput type="number" step="1" min="0" [(ngModel)]="consumable.inputAmountPerRun" style="font-size: 14px; padding: 4px 6px" />
                          </mat-form-field>
                          <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Remove Consumable" (click)="craft.removeConsumable(consumable.inputItem.item)">
                            <mat-icon>close</mat-icon>
                          </button>
                        </div>
                      </div>
                    }
                  </div>
                </div>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateConsumables(craft)">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateConsumables(craft, craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Runs -->
          <ng-container matColumnDef="runs">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Runs</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <mat-form-field style="max-width: 120px">
                  <input matInput type="number" step="1" min="0" [(ngModel)]="craft.numberOfRuns" />
                </mat-form-field>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateRuns(craft.numberOfRuns())">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateRuns(craft.numberOfRuns(), craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Extra Expenses -->
          <ng-container matColumnDef="extraexpenses">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-160">Extra Expenses</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <mat-form-field style="max-width: 120px">
                  <input matInput type="number" step="1" min="0" [(ngModel)]="craft.extraExpenses" />
                </mat-form-field>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateExtraExpenses(craft.extraExpenses())">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateExtraExpenses(craft.extraExpenses(), craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Weight -->
          <ng-container matColumnDef="weight">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Weight</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: column; gap: 4px; align-items: center; justify-content: center">
                <span>Inputs: {{ craft.totalInputsWeight() | number: '1.0-0' }}</span>
                <span>Outputs: {{ craft.totalOutputsWeight() | number: '1.0-0' }}</span>
              </div>
            </td>
          </ng-container>

          <!-- Ignore Journals? -->
          <ng-container matColumnDef="ignorejournals">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Ignore Journals</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <mat-checkbox [(ngModel)]="craft.ignoreJournals"></mat-checkbox>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateIgnoreJournals(craft.ignoreJournals())">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateIgnoreJournals(craft.ignoreJournals(), craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Use Focus? -->
          <ng-container matColumnDef="usefocus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Use Focus</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <mat-checkbox [(ngModel)]="craft.useFocus"></mat-checkbox>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateUseFocus(craft.useFocus())">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateUseFocus(craft.useFocus(), craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- RRR -->
          <ng-container matColumnDef="rrr">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">RRR</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <mat-form-field style="max-width: 120px">
                  <input matInput type="number" step="0.01" min="0" max="100" [value]="craft.rrr() != null ? Math.round(craft.rrr() * 10000) / 100 : ''" (input)="onRrrInput($event, craft)" />
                  <span matSuffix>%</span>
                </mat-form-field>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateRRR(craft.rrr())">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateRRR(craft.rrr(), craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Craft Fee -->
          <ng-container matColumnDef="craftfee">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Craft Fee Per Nutrition</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <mat-form-field style="max-width: 120px">
                  <input matInput type="number" step="1" min="0" max="9999" [(ngModel)]="craft.craftFeePerNutrition" />
                </mat-form-field>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateCraftFee(craft.craftFeePerNutrition())">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateCraftFee(craft.craftFeePerNutrition(), craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Expenses -->
          <ng-container matColumnDef="expenses">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Total Expenses</th>
            <td mat-cell *matCellDef="let craft">
              <span>{{ craft.totalExpenses() | number: '1.0-0' }}</span>
            </td>
          </ng-container>

          <!-- Total Fame -->
          <ng-container matColumnDef="totalfame">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Total Fame</th>
            <td mat-cell *matCellDef="let craft">
              <span>{{ craft.craftFamePlayerTotal() | number: '1.0-0' }}</span>
            </td>
          </ng-container>

          <!-- Fame Per Silver -->
          <ng-container matColumnDef="famepersilver">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Fame Per Silver</th>
            <td mat-cell *matCellDef="let craft">
              <span>{{ craft.craftFamePlayerPerExpenses() | number: '1.2-2' }}</span>
            </td>
          </ng-container>

          <!-- Fame Per Focus -->
          <ng-container matColumnDef="fameperfocus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Fame Per Focus</th>
            <td mat-cell *matCellDef="let craft">
              <span>{{ craft.craftFamePlayerPerFocus() | number: '1.2-2' }}</span>
            </td>
          </ng-container>

          <!-- Total Focus -->
          <ng-container matColumnDef="totalfocus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Total Focus</th>
            <td mat-cell *matCellDef="let craft">
              <span>{{ craft.totalFocus() | number: '1.0-0' }}</span>
            </td>
          </ng-container>

          <!-- Profit Per Focus -->
          <ng-container matColumnDef="profitperfocus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Profit Per Focus</th>
            <td mat-cell *matCellDef="let craft">
              <span>{{ craft.resultPerFocus() | number: '1.0-0' }}</span>
            </td>
          </ng-container>

          <!-- Craft Fee -->
          <ng-container matColumnDef="totalcraftfee">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Total Craft Fee</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: column; align-items: center; justify-content: center">
                <span style="margin-bottom: 3px">{{ craft.totalCraftFee() | number: '1.0-0' }} </span>
              </div>
            </td>
          </ng-container>

          <!-- Result -->
          <ng-container matColumnDef="result">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Profit</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: column; align-items: center; justify-content: center">
                <span style="margin-bottom: 3px">{{ craft.result() | number: '1.0-0' }} </span>
              </div>
            </td>
          </ng-container>

          <!-- Result % -->
          <ng-container matColumnDef="resultpercentage">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-120">Profit %</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: column; align-items: center; justify-content: center">
                <span>{{ craft.resultPercent() | percent: '1.2-2' }}</span>
              </div>
            </td>
          </ng-container>

          <!-- Selected -->
          <ng-container matColumnDef="selected">
            <th mat-header-cell *matHeaderCellDef mat-sort-header class="min-width-80">Craft</th>
            <td mat-cell *matCellDef="let craft">
              <div style="display: flex; flex-direction: row; align-items: center; justify-content: center">
                <mat-checkbox [(ngModel)]="craft.selected"></mat-checkbox>
                <div style="display: flex; flex-direction: column">
                  <button style="color: var(--accent-color)" mat-icon-button class="mat-icon-button small-icon-button" matTooltip="Propagate Value to All Crafts" (click)="propagateSelected(craft.selected())">
                    <mat-icon>share</mat-icon>
                  </button>
                  <button
                    style="color: var(--accent-color)"
                    mat-icon-button
                    class="mat-icon-button small-icon-button"
                    matTooltip="Propagate Value to Crafts Of Same Category"
                    (click)="propagateSelected(craft.selected(), craft.product.groupUniqueName)"
                  >
                    <mat-icon>send</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="craftsDisplayedColumns(); sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: craftsDisplayedColumns()"></tr>
        </table>
      </div>
      <div>
        <mat-paginator #craftsPaginator [pageSizeOptions]="[25, 50, 100]" showFirstLastButtons aria-label="Select page of crafts"> </mat-paginator>
      </div>
    } @placeholder {
      <p>Hold tight</p>
    }
  </app-subsection>
</app-section>
